// Server routes attach a stable, translatable code to user-facing errors:
//   createError({ statusCode, statusMessage: '<English>', data: { code: 'auth.invalidPassword' } })
// The client looks it up as `errors.<code>`. The code can surface in three shapes
// depending on how the request was made:
//   - $fetch FetchError → `error.data` is the H3 body → `error.data.data.code`
//   - NuxtError         → `error.data` is the H3 `data` payload → `error.data.code`
//   - raw response      → `error.response._data.data.code`

export type ErrorParams = Record<string, string | number>;

type CodedPayload = { code: string; params?: unknown };

// Dotted camelCase segments only — never lets arbitrary server text become a key path.
const ERROR_CODE_PATTERN = /^[A-Za-z][A-Za-z0-9]*(\.[A-Za-z][A-Za-z0-9]*)*$/;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const isCodedPayload = (value: unknown): value is CodedPayload => {
  const code = asRecord(value)?.code;
  return typeof code === 'string' && ERROR_CODE_PATTERN.test(code);
};

const findCodedPayload = (error: unknown): CodedPayload | null => {
  const err = asRecord(error);
  if (!err) return null;

  const data = asRecord(err.data);
  const responseBody = asRecord(asRecord(err.response)?._data);
  const candidates = [data?.data, err.data, responseBody?.data];

  return candidates.find(isCodedPayload) ?? null;
};

export const extractErrorCode = (error: unknown): string | null => findCodedPayload(error)?.code ?? null;

export const extractErrorParams = (error: unknown): ErrorParams => {
  const params = asRecord(findCodedPayload(error)?.params);
  if (!params) return {};

  return Object.fromEntries(
    Object.entries(params).filter(
      (entry): entry is [string, string | number] => typeof entry[1] === 'string' || typeof entry[1] === 'number',
    ),
  );
};

// Minimal slice of vue-i18n the resolvers need (kept structural so it's unit-testable).
export type ErrorTranslator = {
  t: (key: string, params?: ErrorParams) => string;
  te: (key: string) => boolean;
};

// `errors.<code>` translated with the payload's params, or null when the error carries no
// code or the code has no catalog entry.
export const resolveCodedMessage = (error: unknown, i18n: ErrorTranslator): string | null => {
  const code = extractErrorCode(error);
  if (!code) return null;

  const key = `errors.${code}`;
  return i18n.te(key) ? i18n.t(key, extractErrorParams(error)) : null;
};

// Display text for an error: the translated coded message, else the caller's existing fallback.
export const resolveErrorMessage = (error: unknown, i18n: ErrorTranslator, fallback: string): string =>
  resolveCodedMessage(error, i18n) ?? fallback;
